import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { getProducts } from "../api/product";
import { getBrands } from "../api/brand";
import { useLocation } from "./LocationContext";

export const ProductsContext = createContext();

export const useProducts = () => {
  const context = useContext(ProductsContext);
  return context;
};

export function ProductsProvider({ children }) {
  const [productsLoading, setProductsLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [error, setError] = useState(null);

  // Get user location
  const { userCountry, loading: locationLoading } = useLocation();

  // Track current country to detect changes
  const currentCountryRef = useRef(null);

  // Cache management - simpler approach
  const cacheRef = useRef(new Map()); // country -> { data, timestamp }
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  // Single fetch promise to prevent duplicates
  const fetchPromiseRef = useRef(null);

  // Clear everything when country changes
  const clearCountryData = useCallback(() => {
    console.log('🧹 Clearing data for country change');
    setProducts([]);
    setError(null);
    // Keep cache for other countries, just clear current state
  }, []);

  // Check if we have valid cached data for a country
  const getCachedData = useCallback((country) => {
    if (!country) return null;

    const cached = cacheRef.current.get(country);
    if (!cached) return null;

    const now = Date.now();
    if (now - cached.timestamp > CACHE_DURATION) {
      cacheRef.current.delete(country);
      return null;
    }

    return cached.data;
  }, []);

  // Set cached data for a country
  const setCachedData = useCallback((country, data) => {
    if (!country) return;

    cacheRef.current.set(country, {
      data,
      timestamp: Date.now()
    });
  }, []);

  // Main fetch function
  const fetchProducts = useCallback(async (forceRefresh = false) => {
    // Wait for location to be determined
    if (locationLoading || !userCountry) {
      return [];
    }

    // Check cache first (unless forcing refresh)
    if (!forceRefresh) {
      const cachedData = getCachedData(userCountry);
      if (cachedData) {
        console.log('📦 Using cached data for', userCountry);
        setProducts(cachedData);
        return cachedData;
      }
    }

    // Prevent duplicate requests
    if (fetchPromiseRef.current && !forceRefresh) {
      console.log('⏳ Fetch already in progress, waiting...');
      return fetchPromiseRef.current;
    }

    console.log('🌐 Fetching products for country:', userCountry);
    setProductsLoading(true);
    setError(null);

    const fetchPromise = (async () => {
      try {
        const response = await getProducts(1, 1000, userCountry);

        if (response?.docs) {
          console.log('✅ Products fetched successfully:', response.docs.length, 'items');

          // Only update state if we're still on the same country
          if (userCountry === currentCountryRef.current) {
            setProducts(response.docs);
            setCachedData(userCountry, response.docs);
          } else {
            console.log('🔄 Country changed during fetch, discarding results');
          }

          return response.docs;
        } else {
          throw new Error("No products found");
        }
      } catch (error) {
        console.error("❌ Error fetching products:", error);

        // Only update error state if we're still on the same country
        if (userCountry === currentCountryRef.current) {
          setError("Failed to fetch products: " + error.message);
        }

        return [];
      } finally {
        setProductsLoading(false);
        fetchPromiseRef.current = null;
      }
    })();

    fetchPromiseRef.current = fetchPromise;
    return fetchPromise;
  }, [userCountry, locationLoading, getCachedData, setCachedData]);

  // Handle country changes
  useEffect(() => {
    if (locationLoading || !userCountry) return;

    const hasCountryChanged = currentCountryRef.current !== userCountry;

    if (hasCountryChanged) {
      console.log('🌍 Country changed:', currentCountryRef.current, '->', userCountry);

      // Update the ref immediately
      currentCountryRef.current = userCountry;

      // Clear current data
      clearCountryData();

      // Cancel any ongoing fetch
      fetchPromiseRef.current = null;

      // Fetch new data
      fetchProducts();
    }
  }, [userCountry, locationLoading, clearCountryData, fetchProducts]);

  // Initial load
  useEffect(() => {
    if (!locationLoading && userCountry && !currentCountryRef.current) {
      console.log('🚀 Initial load for country:', userCountry);
      currentCountryRef.current = userCountry;
      fetchProducts();
    }
  }, [userCountry, locationLoading, fetchProducts]);

  // Fetch brands (simpler, no country dependency)
  const fetchBrands = useCallback(async () => {
    if (brands.length > 0) return brands;

    try {
      const response = await getBrands();
      if (response?.docs) {
        setBrands(response.docs);
        return response.docs;
      }
    } catch (error) {
      console.error("Error fetching brands:", error);
    }
    return [];
  }, [brands.length]);

  // Cache utilities
  const clearCache = useCallback(() => {
    console.log('🗑️ Clearing all cache');
    cacheRef.current.clear();
    clearCountryData();
  }, [clearCountryData]);

  const refreshData = useCallback(() => {
    console.log('🔄 Refreshing data for current country');
    return fetchProducts(true);
  }, [fetchProducts]);

  const getCacheInfo = useCallback(() => {
    const cacheEntries = Array.from(cacheRef.current.entries()).map(([country, cache]) => ({
      country,
      itemCount: cache.data.length,
      age: Date.now() - cache.timestamp,
      isValid: (Date.now() - cache.timestamp) < CACHE_DURATION
    }));

    return {
      currentCountry: userCountry,
      currentProductsCount: products.length,
      cacheEntries,
      isLoading: productsLoading,
      hasError: !!error
    };
  }, [userCountry, products.length, productsLoading, error]);

  const value = {
    // Core data
    products,
    brands,
    productsLoading,
    error,

    // Functions
    fetchProducts,
    fetchBrands,
    refreshData,

    // Cache management
    clearCache,
    getCacheInfo,

    // Legacy compatibility
    searchProducts: products,
    fullProducts: products,
    fetchSearchProducts: fetchProducts,
    fetchFullProducts: fetchProducts
  };

  return (
    <ProductsContext.Provider value={value}>
      {children}
    </ProductsContext.Provider>
  );
}